import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Canvas, FabricImage, FabricObject, IText } from 'fabric';
import {
  DEFAULT_ANIMATION_CONFIG,
  playAllAnimations as runAllAnimations,
  type AnimatableObject,
  type AnimationConfig,
} from './CanvasAnimations';
import { getLayerObjects, isTemplateLayer, reorderLayers, type LayerObject, type LayerSourceRef } from './CanvasLayers';
import { loadCanvasLayers, type CanvasStructure } from './CanvasDocument';
import { toSecureImageUrl } from '@/utils/imageUrl';
import { captureHistorySnapshot, restoreHistorySnapshot, type HistoryEntry } from './CanvasHistory';

// Idle time to wait before grouping continuous changes (dragging the color picker,
// moving a slider, typing) into a single history entry.
const HISTORY_DEBOUNCE_MS = 500;
const MAX_HISTORY_STEPS = 50;

// Editor zoom limits.
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 5;

// Border and corner color when selecting any canvas object.
// Overridden once, globally, because IText/FabricImage/etc. take
// this value from FabricObject.ownDefaults on construction (no need to set it per object).
FabricObject.ownDefaults.borderColor = '#FFCC00';
FabricObject.ownDefaults.cornerColor = '#FFCC00';

export interface SelectedTextStyle {
  fontFamily?: string;
  fontWeight?: number;
  fontSize?: number;
  fontStyle?: 'normal' | 'italic';
  underline?: boolean;
  fill?: string;
  textAlign?: 'left' | 'center' | 'right';
  // Url of the font file, saved with the text so its font can be loaded when the canvas is opened again.
  fontUrl?: string;
}

interface CanvasEditorContextValue {
  canvas: Canvas | null;
  registerCanvas: (canvas: Canvas | null) => void;
  addImageFromUrl: (
    url: string,
    position?: { x: number; y: number },
    name?: string,
    sourceRef?: LayerSourceRef
  ) => Promise<void>;
  // False until the saved layers are on the canvas; the history (and the autosave) start from there.
  documentReady: boolean;
  // Saved layers that couldn't be rebuilt (e.g. an image that no longer exists).
  unloadedLayerCount: number;
  addText: () => void;
  applyTextStyle: (style: SelectedTextStyle) => void;
  selectedObject: FabricObject | null;
  selectedObjectVersion: number;
  setSelectedObject: (object: FabricObject | null) => void;
  refreshSelectedObject: () => void;
  zoom: number;
  setZoomLevel: (zoom: number) => void;
  zoomToFit: () => void;
  registerZoomToFit: (fn: (() => void) | null) => void;
  gridEnabled: boolean;
  setGridEnabled: (value: boolean) => void;
  safeZoneEnabled: boolean;
  setSafeZoneEnabled: (value: boolean) => void;
  safeZoneMarginPercent: number;
  setSafeZoneMarginPercent: (value: number) => void;
  snapToGridEnabled: boolean;
  setSnapToGridEnabled: (value: boolean) => void;
  backgroundColor: string;
  setBackgroundColor: (color: string) => void;
  canvasWidth: number;
  canvasHeight: number;
  canvasSizeId: string;
  setCanvasSize: (width: number, height: number, sizeId: string) => void;
  activeRailTab: string;
  setActiveRailTab: (id: string) => void;
  applyAnimation: (config: Partial<AnimationConfig>) => void;
  removeAnimation: () => void;
  playAllAnimations: () => void;
  historyEntries: HistoryEntry[];
  historyIndex: number;
  canUndo: boolean;
  canRedo: boolean;
  recordHistory: (label: string, options?: { debounce?: boolean }) => void;
  undo: () => void;
  redo: () => void;
  jumpToHistory: (index: number) => void;
}

const CanvasEditorContext = createContext<CanvasEditorContextValue | null>(null);

export interface CanvasInitialConfig {
  width: number;
  height: number;
  sizeId: string;
  backgroundColor?: string;
  // Saved document to rebuild once the canvas is ready.
  structure?: CanvasStructure | null;
}

interface CanvasEditorProviderProps {
  children: ReactNode;
  initialConfig?: CanvasInitialConfig;
}

// Shares a single fabric.Canvas instance between the toolbar, the side panel and the stage.
export const CanvasEditorProvider = ({ children, initialConfig }: CanvasEditorProviderProps) => {
  const [canvas, setCanvas] = useState<Canvas | null>(null);
  const canvasRef = useRef<Canvas | null>(null);
  const [zoom, setZoom] = useState(1);
  const zoomToFitRef = useRef<(() => void) | null>(null);
  const [gridEnabled, setGridEnabled] = useState(false);
  const [safeZoneEnabled, setSafeZoneEnabled] = useState(false);
  const [safeZoneMarginPercentState, setSafeZoneMarginPercentState] = useState(20);
  const [snapToGridEnabled, setSnapToGridEnabled] = useState(false);
  const [backgroundColorState, setBackgroundColorState] = useState(initialConfig?.backgroundColor ?? '#ffffff');
  const [canvasWidth, setCanvasWidth] = useState(initialConfig?.width ?? 1080);
  const [canvasHeight, setCanvasHeight] = useState(initialConfig?.height ?? 1080);
  const [canvasSizeId, setCanvasSizeId] = useState(initialConfig?.sizeId ?? 'square');
  const [activeRailTab, setActiveRailTab] = useState('template');
  const [selectedObjectState, setSelectedObjectState] = useState<FabricObject | null>(null);
  const [selectedObjectVersion, setSelectedObjectVersion] = useState(0);
  const [history, setHistory] = useState<{ stack: HistoryEntry[]; index: number }>({ stack: [], index: -1 });
  const isRestoringHistoryRef = useRef(false);
  const historyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set while a compound change (e.g. replacing the template) runs, so it ends up as a single history entry.
  const suppressHistoryRef = useRef(false);
  const savedLayersRef = useRef(initialConfig?.structure?.layers ?? []);
  const [documentReady, setDocumentReady] = useState(false);
  const [unloadedLayerCount, setUnloadedLayerCount] = useState(0);
  // Always up-to-date copy of the document state, read by registerCanvas/commitHistorySnapshot
  // (keeping them out of their dependencies avoids recreating the canvas whenever they change).
  const documentStateRef = useRef({ backgroundColor: backgroundColorState, canvasWidth, canvasHeight, canvasSizeId });
  documentStateRef.current = { backgroundColor: backgroundColorState, canvasWidth, canvasHeight, canvasSizeId };

  const commitHistorySnapshot = useCallback((label: string) => {
    const activeCanvas = canvasRef.current;
    if (!activeCanvas || isRestoringHistoryRef.current) return;

    const snapshot = captureHistorySnapshot(activeCanvas, documentStateRef.current);
    setHistory((prev) => {
      const truncated = prev.stack.slice(0, prev.index + 1);
      const nextStack = [...truncated, { label, timestamp: Date.now(), snapshot }].slice(-MAX_HISTORY_STEPS);
      return { stack: nextStack, index: nextStack.length - 1 };
    });
  }, []);

  // debounce: true groups continuous changes (dragging the color picker, a slider, typing)
  // into a single entry; discrete actions (add/delete/reorder/lock) are
  // recorded immediately.
  const recordHistory = useCallback(
    (label: string, options?: { debounce?: boolean }) => {
      if (suppressHistoryRef.current) return;
      if (historyDebounceRef.current) {
        clearTimeout(historyDebounceRef.current);
        historyDebounceRef.current = null;
      }
      if (!options?.debounce) {
        commitHistorySnapshot(label);
        return;
      }
      historyDebounceRef.current = setTimeout(() => {
        historyDebounceRef.current = null;
        commitHistorySnapshot(label);
      }, HISTORY_DEBOUNCE_MS);
    },
    [commitHistorySnapshot]
  );

  const applyHistoryEntry = useCallback(
    async (index: number, stack: HistoryEntry[]) => {
      const activeCanvas = canvasRef.current;
      const entry = stack[index];
      if (!activeCanvas || !entry) return;

      isRestoringHistoryRef.current = true;
      setBackgroundColorState(entry.snapshot.backgroundColor);
      setCanvasWidth(entry.snapshot.canvasWidth);
      setCanvasHeight(entry.snapshot.canvasHeight);
      setCanvasSizeId(entry.snapshot.canvasSizeId);
      await restoreHistorySnapshot(activeCanvas, entry.snapshot);
      setSelectedObject(null);
      isRestoringHistoryRef.current = false;
    },
    // setSelectedObject is defined further below but its identity is stable (useCallback with []).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const undo = useCallback(() => {
    if (history.index <= 0) return;
    const nextIndex = history.index - 1;
    setHistory({ stack: history.stack, index: nextIndex });
    void applyHistoryEntry(nextIndex, history.stack);
  }, [history, applyHistoryEntry]);

  const redo = useCallback(() => {
    if (history.index >= history.stack.length - 1) return;
    const nextIndex = history.index + 1;
    setHistory({ stack: history.stack, index: nextIndex });
    void applyHistoryEntry(nextIndex, history.stack);
  }, [history, applyHistoryEntry]);

  const jumpToHistory = useCallback(
    (index: number) => {
      if (index < 0 || index >= history.stack.length || index === history.index) return;
      setHistory({ stack: history.stack, index });
      void applyHistoryEntry(index, history.stack);
    },
    [history, applyHistoryEntry]
  );


  const setSelectedObject = useCallback((object: FabricObject | null) => {
    setSelectedObjectState(object);
    setSelectedObjectVersion((v) => v + 1);
  }, []);

  const refreshSelectedObject = useCallback(() => {
    setSelectedObjectVersion((v) => v + 1);
  }, []);

  const setCanvasSize = useCallback((width: number, height: number, sizeId: string) => {
    setCanvasWidth(width);
    setCanvasHeight(height);
    setCanvasSizeId(sizeId);
    recordHistory('Cambiar tamaño del canvas', { debounce: true });
  }, [recordHistory]);

  const setBackgroundColor = useCallback((color: string) => {
    setBackgroundColorState(color);
    recordHistory('Cambiar color de fondo', { debounce: true });
  }, [recordHistory]);

  const setSafeZoneMarginPercent = useCallback((value: number) => {
    // This is the TOTAL crop (both sides combined), hence the 90% cap rather than 50%.
    setSafeZoneMarginPercentState(Math.max(1, Math.min(90, value)));
  }, []);

  const registerCanvas = useCallback((next: Canvas | null) => {
    canvasRef.current = next;
    setCanvas(next);
    setDocumentReady(false);
    if (!next) {
      setHistory({ stack: [], index: -1 });
      return;
    }

    const startHistory = () => {
      const snapshot = captureHistorySnapshot(next, documentStateRef.current);
      setHistory({ stack: [{ label: 'Estado inicial', timestamp: Date.now(), snapshot }], index: 0 });
      setDocumentReady(true);
    };

    const savedLayers = savedLayersRef.current;
    if (savedLayers.length === 0) {
      startHistory();
      return;
    }

    // Adding the saved layers must not record history: the loaded document is the initial state.
    isRestoringHistoryRef.current = true;
    loadCanvasLayers(next, savedLayers)
      .then(setUnloadedLayerCount)
      .catch((error: unknown) => {
        console.error('Error al cargar el canvas:', error);
        setUnloadedLayerCount(savedLayers.length);
      })
      .finally(() => {
        // A newer canvas replaced this one meanwhile (StrictMode/HMR remount): its own load takes over.
        if (canvasRef.current !== next) return;
        isRestoringHistoryRef.current = false;
        next.requestRenderAll();
        startHistory();
      });
  }, []);

  const setZoomLevel = useCallback((next: number) => {
    setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next)));
  }, []);

  const registerZoomToFit = useCallback((fn: (() => void) | null) => {
    zoomToFitRef.current = fn;
  }, []);

  const zoomToFit = useCallback(() => {
    zoomToFitRef.current?.();
  }, []);

  const addImageFromUrl = useCallback(async (
    url: string,
    position?: { x: number; y: number },
    name?: string,
    sourceRef?: LayerSourceRef
  ) => {
    const activeCanvas = canvasRef.current;
    if (!activeCanvas) return;

    const image = await FabricImage.fromURL(toSecureImageUrl(url), { crossOrigin: 'anonymous' });

    const maxSize = 300;
    if (image.width && image.height && Math.max(image.width, image.height) > maxSize) {
      const scale = maxSize / Math.max(image.width, image.height);
      image.scale(scale);
    }

    image.set({
      left: position?.x ?? activeCanvas.getWidth() / 2,
      top: position?.y ?? activeCanvas.getHeight() / 2,
    });
    if (name) (image as LayerObject).layerName = name;
    if (sourceRef) (image as LayerObject).sourceRef = sourceRef;

    // A canvas has a single template: a new one takes the place (layer position) of the current one,
    // recorded as one history entry instead of a removal plus an addition.
    const currentTemplate = sourceRef?.kind === 'template' ? getLayerObjects(activeCanvas).find(isTemplateLayer) : undefined;
    if (currentTemplate) {
      const layers = getLayerObjects(activeCanvas);
      layers.splice(layers.indexOf(currentTemplate), 1, image);
      suppressHistoryRef.current = true;
      try {
        activeCanvas.remove(currentTemplate);
        activeCanvas.add(image);
        reorderLayers(activeCanvas, layers);
      } finally {
        suppressHistoryRef.current = false;
      }
      activeCanvas.setActiveObject(image);
      activeCanvas.requestRenderAll();
      recordHistory('Reemplazar template');
      return;
    }

    activeCanvas.add(image);
    activeCanvas.setActiveObject(image);
    activeCanvas.requestRenderAll();
  }, [recordHistory]);

  const addText = useCallback(() => {
    const activeCanvas = canvasRef.current;
    if (!activeCanvas) return;

    const text = new IText('Texto de prueba', {
      left: activeCanvas.getWidth() / 2,
      top: activeCanvas.getHeight() / 2,
      fontSize: 24,
      fill: '#202020',
    });

    activeCanvas.add(text);
    activeCanvas.setActiveObject(text);
    activeCanvas.requestRenderAll();
  }, []);

  const applyTextStyle = useCallback((style: SelectedTextStyle) => {
    const activeCanvas = canvasRef.current;
    if (!activeCanvas) return;

    const activeObject = activeCanvas.getActiveObject();
    if (!activeObject || !('fontFamily' in activeObject)) return;

    activeObject.set(style);
    activeCanvas.requestRenderAll();
    setSelectedObjectVersion((v) => v + 1);
    recordHistory('Editar texto', { debounce: true });
  }, [recordHistory]);

  const applyAnimation = useCallback((config: Partial<AnimationConfig>) => {
    const activeCanvas = canvasRef.current;
    const activeObject = activeCanvas?.getActiveObject() as AnimatableObject | undefined;
    if (!activeCanvas || !activeObject) return;

    activeObject.animationConfig = { ...DEFAULT_ANIMATION_CONFIG, ...activeObject.animationConfig, ...config };
    setSelectedObjectVersion((v) => v + 1);
    recordHistory('Editar animación', { debounce: true });
  }, [recordHistory]);

  const removeAnimation = useCallback(() => {
    const activeObject = canvasRef.current?.getActiveObject() as AnimatableObject | undefined;
    if (!activeObject) return;

    delete activeObject.animationConfig;
    setSelectedObjectVersion((v) => v + 1);
    recordHistory('Eliminar animación');
  }, [recordHistory]);

  const playAllAnimations = useCallback(() => {
    if (canvasRef.current) runAllAnimations(canvasRef.current);
  }, []);

  const value = useMemo(
    () => ({
      canvas,
      registerCanvas,
      addImageFromUrl,
      documentReady,
      unloadedLayerCount,
      addText,
      applyTextStyle,
      selectedObject: selectedObjectState,
      selectedObjectVersion,
      setSelectedObject,
      refreshSelectedObject,
      zoom,
      setZoomLevel,
      zoomToFit,
      registerZoomToFit,
      gridEnabled,
      setGridEnabled,
      safeZoneEnabled,
      setSafeZoneEnabled,
      safeZoneMarginPercent: safeZoneMarginPercentState,
      setSafeZoneMarginPercent,
      snapToGridEnabled,
      setSnapToGridEnabled,
      backgroundColor: backgroundColorState,
      setBackgroundColor,
      canvasWidth,
      canvasHeight,
      canvasSizeId,
      setCanvasSize,
      activeRailTab,
      setActiveRailTab,
      applyAnimation,
      removeAnimation,
      playAllAnimations,
      historyEntries: history.stack,
      historyIndex: history.index,
      canUndo: history.index > 0,
      canRedo: history.index < history.stack.length - 1,
      recordHistory,
      undo,
      redo,
      jumpToHistory,
    }),
    [
      canvas,
      registerCanvas,
      addImageFromUrl,
      documentReady,
      unloadedLayerCount,
      addText,
      applyTextStyle,
      selectedObjectState,
      selectedObjectVersion,
      setSelectedObject,
      refreshSelectedObject,
      zoom,
      setZoomLevel,
      zoomToFit,
      registerZoomToFit,
      gridEnabled,
      safeZoneEnabled,
      safeZoneMarginPercentState,
      setSafeZoneMarginPercent,
      snapToGridEnabled,
      backgroundColorState,
      canvasWidth,
      canvasHeight,
      canvasSizeId,
      setCanvasSize,
      activeRailTab,
      applyAnimation,
      removeAnimation,
      playAllAnimations,
      history,
      recordHistory,
      undo,
      redo,
      jumpToHistory,
    ]
  );

  return <CanvasEditorContext.Provider value={value}>{children}</CanvasEditorContext.Provider>;
};

export const useCanvasEditor = () => {
  const context = useContext(CanvasEditorContext);
  if (!context) throw new Error('useCanvasEditor debe usarse dentro de un CanvasEditorProvider');
  return context;
};

